/**
 * Tile-grid layout for the state coverage map.
 *
 * A tile cartogram keeps every state clickable at the same size (no tiny New
 * England squares) and needs no external basemap or paid geo service, which
 * keeps the directory on free public data only.
 */
import { COUNTY_RECORD_HUBS, type CountyRecordHub } from "@/data/countyRecordHubs";
import { PUBLIC_RECORDS } from "@/data/publicRecordsDirectory";
import { hubHasGeometry } from "@/lib/public-records-geometry";

export interface StateTile {
  code: string;
  /** Column in the tile grid (0-based). */
  col: number;
  /** Row in the tile grid (0-based). */
  row: number;
}

/** Standard US tile-grid arrangement, 11 columns x 8 rows. */
export const STATE_TILES: StateTile[] = [
  { code: "AK", col: 0, row: 0 },
  { code: "ME", col: 10, row: 0 },
  { code: "VT", col: 9, row: 1 },
  { code: "NH", col: 10, row: 1 },
  { code: "WA", col: 1, row: 1 },
  { code: "ID", col: 2, row: 1 },
  { code: "MT", col: 3, row: 1 },
  { code: "ND", col: 4, row: 1 },
  { code: "MN", col: 5, row: 1 },
  { code: "IL", col: 6, row: 1 },
  { code: "WI", col: 7, row: 1 },
  { code: "MI", col: 8, row: 1 },
  { code: "NY", col: 9, row: 2 },
  { code: "MA", col: 10, row: 2 },
  { code: "OR", col: 1, row: 2 },
  { code: "NV", col: 2, row: 2 },
  { code: "WY", col: 3, row: 2 },
  { code: "SD", col: 4, row: 2 },
  { code: "IA", col: 5, row: 2 },
  { code: "IN", col: 6, row: 2 },
  { code: "OH", col: 7, row: 2 },
  { code: "PA", col: 8, row: 2 },
  { code: "NJ", col: 9, row: 3 },
  { code: "CT", col: 10, row: 3 },
  { code: "RI", col: 10, row: 4 },
  { code: "CA", col: 1, row: 3 },
  { code: "UT", col: 2, row: 3 },
  { code: "CO", col: 3, row: 3 },
  { code: "NE", col: 4, row: 3 },
  { code: "MO", col: 5, row: 3 },
  { code: "KY", col: 6, row: 3 },
  { code: "WV", col: 7, row: 3 },
  { code: "MD", col: 8, row: 3 },
  { code: "DE", col: 9, row: 4 },
  { code: "AZ", col: 2, row: 4 },
  { code: "NM", col: 3, row: 4 },
  { code: "KS", col: 4, row: 4 },
  { code: "AR", col: 5, row: 4 },
  { code: "TN", col: 6, row: 4 },
  { code: "VA", col: 7, row: 4 },
  { code: "NC", col: 8, row: 4 },
  { code: "OK", col: 4, row: 5 },
  { code: "LA", col: 5, row: 5 },
  { code: "MS", col: 6, row: 5 },
  { code: "AL", col: 7, row: 5 },
  { code: "SC", col: 8, row: 5 },
  { code: "HI", col: 0, row: 6 },
  { code: "TX", col: 4, row: 6 },
  { code: "GA", col: 7, row: 6 },
  { code: "FL", col: 8, row: 6 },
  { code: "DC", col: 9, row: 5 },
];

/** Coverage strength used to shade a tile. */
export type MapTier = "geometry" | "records" | "none";

export interface StateMapDatum {
  code: string;
  name: string;
  tier: MapTier;
  countyHubs: number;
  /** County hubs publishing spatial layers. */
  geometryCounties: number;
  sources: number;
  /** County hubs of this state, geometry-first. */
  hubs: CountyRecordHub[];
}

export const MAP_TIER_LABEL: Record<MapTier, string> = {
  geometry: "Counties with geometry",
  records: "Records only (no geometry)",
  none: "Not indexed yet",
};

/** Semantic token classes for the map legend and tiles. */
export const MAP_TIER_CLASS: Record<MapTier, string> = {
  geometry: "fill-brass/80",
  records: "fill-muted-foreground/40",
  none: "fill-muted/50",
};

export function buildStateMapData(
  hubs: CountyRecordHub[] = COUNTY_RECORD_HUBS,
  records = PUBLIC_RECORDS,
): Record<string, StateMapDatum> {
  const names = new Map<string, string>();
  for (const hub of hubs) names.set(hub.state, hub.stateName);
  for (const record of records) {
    if (record.state !== "US") names.set(record.state, record.stateName);
  }

  const out: Record<string, StateMapDatum> = {};
  for (const tile of STATE_TILES) {
    const stateHubs = hubs.filter((h) => h.state === tile.code);
    const geometryCounties = stateHubs.filter(hubHasGeometry).length;
    const sources = records.filter((r) => r.state === tile.code).length;
    out[tile.code] = {
      code: tile.code,
      name: names.get(tile.code) ?? tile.code,
      tier: geometryCounties > 0 ? "geometry" : stateHubs.length || sources ? "records" : "none",
      countyHubs: stateHubs.length,
      geometryCounties,
      sources,
      hubs: [...stateHubs].sort(
        (a, b) => Number(hubHasGeometry(b)) - Number(hubHasGeometry(a)),
      ),
    };
  }
  return out;
}
